import { inject, Injector } from '@angular/core';
import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpEvent, HttpErrorResponse } from '@angular/common/http';
import { AuthStore } from '@app/core/auth/stores/auth-store';
import { AuthService } from '@app/core/auth/services/auth.service';
import { Observable, catchError, throwError, EMPTY, from, of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { Router } from '@angular/router';
import { devLog, devWarn, devError } from '@app/core/utils/dev-logger';

/**
 * HTTP Error Interceptor
 *
 * Handles authentication errors (401/403) globally:
 * 1. Detects session expiration
 * 2. Attempts token refresh first
 * 3. If refresh fails, locks session with return URL
 * 4. Prevents infinite loops and race conditions
 *
 * Other errors are passed through to components.
 */

// Global state for preventing concurrent session expiration handling
let isHandlingSessionExpiration = false;
let sessionExpirationPromise: Promise<void> | null = null;

export const httpErrorInterceptor: HttpInterceptorFn = (
  req: HttpRequest<any>,
  next: HttpHandlerFn
): Observable<HttpEvent<any>> => {
  // Inject at interceptor level (valid injection context)
  const injector = inject(Injector);

  return next(req).pipe(
    catchError((error: any) => {
      // Only handle authentication errors
      if (!(error instanceof HttpErrorResponse)) {
        return throwError(() => error);
      }

      // Handle 401 (Unauthorized) and 403 (Forbidden) errors
      if (error.status === 401 || error.status === 403) {
        // Get dependencies from injector (not inject() which requires injection context)
        try {
          const authStore = injector.get(AuthStore);
          const authService = injector.get(AuthService);
          const router = injector.get(Router);
          return handleAuthError(error, req, next, authStore, authService, router);
        } catch (err) {
          // If we can't get the dependencies (during initialization), just pass through the error
          devWarn('⚠️ HTTP Error Interceptor: Dependencies not available yet, passing through error');
          return throwError(() => error);
        }
      }

      // For other errors, let them bubble up to components
      return throwError(() => error);
    })
  );
};

/**
 * Handle authentication errors with token refresh and session locking
 */
function handleAuthError(
  error: HttpErrorResponse,
  req: HttpRequest<any>,
  next: HttpHandlerFn,
  authStore: AuthStore,
  authService: AuthService,
  router: Router
): Observable<HttpEvent<any>> {
  const currentUrl = router.url;

  devWarn(`🔐 HTTP Error Interceptor: ${error.status} error on ${req.url}`);

  // Don't handle auth errors if already on login/locked pages (prevents loops)
  if (currentUrl.startsWith('/login') || currentUrl.startsWith('/locked')) {
    devLog('Already on login/locked page, not handling auth error');
    return EMPTY;
  }

  // Don't retry auth endpoints (prevents infinite loops)
  if (req.url.includes('/auth/login') || req.url.includes('/auth/refresh')) {
    devLog('Auth endpoint failed, not retrying');
    return throwError(() => error);
  }

  // If session expiration is already being handled, wait for it
  if (isHandlingSessionExpiration && sessionExpirationPromise) {
    devLog('Session expiration already being handled, waiting...');
    return from(sessionExpirationPromise).pipe(
      switchMap(() => EMPTY)
    );
  }

  // Start handling session expiration
  isHandlingSessionExpiration = true;
  sessionExpirationPromise = handleSessionExpiration(authStore, authService, router, currentUrl);

  return from(sessionExpirationPromise).pipe(
    switchMap(() => {
      // After handling, return empty to prevent error propagation
      return EMPTY;
    }),
    catchError(() => {
      // If something goes wrong, still return empty
      return EMPTY;
    })
  );
}

/**
 * Handle session expiration: Try refresh first, then lock session
 */
async function handleSessionExpiration(
  authStore: AuthStore,
  authService: AuthService,
  router: Router,
  returnUrl: string
): Promise<void> {
  try {
    devLog('🔄 HTTP Error Interceptor: Attempting token refresh...');

    // Step 1: Try to refresh the token
    const refreshSuccess = await authService.refreshToken();

    if (refreshSuccess) {
      devLog('✅ HTTP Error Interceptor: Token refresh succeeded');
      // Token refreshed successfully - request will be retried automatically
      return;
    }

    // Step 2: Refresh failed, lock session
    devWarn('⚠️ HTTP Error Interceptor: Token refresh failed, locking session');
    authService.lockSession(returnUrl);

  } catch (error) {
    devError('❌ HTTP Error Interceptor: Error during session expiration handling:', error);
    // Fallback: lock session
    authService.lockSession(returnUrl);
  } finally {
    // Reset flags after a short delay to allow concurrent requests to complete
    setTimeout(() => {
      isHandlingSessionExpiration = false;
      sessionExpirationPromise = null;
      devLog('🔓 HTTP Error Interceptor: Reset session expiration flags');
    }, 1000);
  }
}
